import React, { useMemo } from 'react';
import { Page, Text, View, Document, Font } from '@react-pdf/renderer';
import notoSansArabic from 'fonts/NotoSansArabic-Regular.ttf';
import RobotoBold from 'fonts/Roboto-Medium.ttf';
import RobotoRegular from 'fonts/Roboto-Regular.ttf';
import RobotoItalic from 'fonts/Roboto-Italic.ttf';
import RobotoBoldItalic from 'fonts/Roboto-BoldItalic.ttf';
import { IntlShape } from 'react-intl';
import {
  AssignedLawyer,
  RcCorporeal,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/typings';
import { translate } from 'containers/services/translate';
import messages from 'containers/CourtManagerLegalProceedings/Provision/Regulation/messages';
import { get, isEmpty } from 'lodash';
import { NORMAL_DATE_FORMAT } from 'constants/global';
import moment from 'moment-timezone';
import {
  REGULATIONS,
  REGULATION_AMOUNTS,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/data-structure';
import {
  FEES,
  PROVISION_ITEM,
  RECIPIENT,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import {
  getTotalAmount,
  getTotalEncryptedDocument,
  getIDDInfo,
  numberWithCommas,
  getRegulationAmount,
  getCNRAAmounts,
  getRiskCorpoProvision,
  getVictimType,
  isStopEntry,
  getVictimFieldLength,
  getCurrentRegulationAmount,
  resolveValue,
  getSummarySheetAmounts,
  getNatureCodeTitle,
  getTotalAmountWithoutRC,
  resolveNewProvision,
  resolveJudgementDateSummarySheet,
  removeCommaSeperator,
  getSumOfRegulations,
  getSumOfContractorFees,
  formatDate,
  getResponsibilityShare,
} from 'containers/CourtManagerLegalProceedings/Provision/Regulation/utils';
import { NONE } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/constants';
import { Listings } from 'containers/AppContainer/typings';
import { styles } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/styled-components';
import { enableRemainingAmountField } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/utils';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { PROCEDURE_STATUSES } from 'containers/data-structure';
import { getCnraTitle } from 'containers/CourtManagerLegalProceedings/Provision/Regulation/Recap/Sheets/utils';
import { isDesactivateIntervenant } from 'services/utils';
import { getValueByCode } from 'containers/CourtManagerLegalProceedings/Provision/utils';
import { ClaimRecapType } from 'typings/claimTypes';

Font.register({
  family: 'NotoSansArabic',
  src: notoSansArabic,
});
Font.register({
  family: 'Roboto',
  src: RobotoRegular,
  fontWeight: 'normal',
  fonts: [
    {
      src: RobotoBold,
      fontWeight: 'bold',
    },
    {
      src: RobotoRegular,
      fontWeight: 'normal',
    },
    {
      src: RobotoItalic,
      fontWeight: 'normal',
      fontStyle: 'italic',
    },
    {
      src: RobotoBoldItalic,
      fontWeight: 'bold',
      fontStyle: 'italic',
    },
  ],
});
export interface SummarySheetProps {
  intl: IntlShape;
  rcCorpo: RcCorporeal;
  claimRecap: ClaimRecapType;
  judgementOnMerits: any;
  sagisClaim: any;
  corpoEncryptedDocumentsResult: any;
  listings: Listings;
  assignedLawyers: AssignedLawyer[];
  encryptedDocumentsOfAdults: any;
  encryptedDocumentsOfMinors: any;
  provisionInfo: any;
  setProvisionInfo: Function;
  hasJom: boolean;
  corpoRegulations: any;
  regulations: any;
  lastSagisClaim: any;
  isDraft: boolean;
}
export const SummarySheet: React.FC<SummarySheetProps> = (
  props,
): JSX.Element => {
  const { oldProvision, riskProvision } = getRiskCorpoProvision(
    props.sagisClaim,
    get(props.rcCorpo, 'riskID'),
  );
  const { cnraCompensationAmount, cnraInterestsAmount } = useMemo(
    () => getCNRAAmounts(props.corpoEncryptedDocumentsResult),
    [props.corpoEncryptedDocumentsResult],
  );
  const { appealDate, judgementDate } = resolveJudgementDateSummarySheet(
    props.rcCorpo,
  );
  const showRemainingAmountField = enableRemainingAmountField(
    get(props.rcCorpo, 'regulationInfo.natureCode'),
  );
  const isAppeal = get(props.rcCorpo.regulationInfo, REGULATIONS.IS_APPEAL);

  const {
    compensationAmount,
    directIndemnityCostAmount,
    fees,
    interestsAmount,
  } = useMemo(
    () =>
      getRegulationAmount(props.rcCorpo, props.corpoEncryptedDocumentsResult),
    [props.corpoEncryptedDocumentsResult],
  );
  const secondFields = [
    {
      items: [
        {
          title: translate(messages.validatorManager, props),
          value: '',
          withBottomBorder: true,
          width: '40',
        },
        {
          title: get(props.rcCorpo.regulationInfo, REGULATIONS.VALIDATOR, ''),
          value: '',
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.visa, props),
          value: '',
          withBottomBorder: true,
          width: '20',
        },
      ],
    },
    {
      items: [
        {
          title: 'Garantie acquise',
          value: !!get(props.claimRecap, 'claim.disclaimerWarrantyReasonId')
            ? translate(messages.no, props)
            : translate(messages.yes, props),
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.oldProvision, props),
          value: props.isDraft
            ? '-'
            : featureService.hasFeature(FEATURES.REGULATION_V2)
            ? oldProvision
            : riskProvision,
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
    {
      items: [
        {
          title: translate(messages.liabilityAssured, props),
          value: `${getResponsibilityShare(props.rcCorpo)}%`,
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.newProvision, props),
          value: props.isDraft
            ? '-'
            : resolveNewProvision(
                props.lastSagisClaim,
                get(props.rcCorpo, 'riskID', ''),
              ),
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
    {
      items: [
        {
          title: translate(messages.withAppeal, props),
          value:
            get(props.rcCorpo.regulationInfo, REGULATIONS.IS_APPEAL, false) ||
            get(props.rcCorpo?.regulationInfo, 'status', '') ===
              PROCEDURE_STATUSES.APPEL ||
            get(props.rcCorpo?.regulationInfo, 'withContestation', '')
              ? translate(messages.yes, props)
              : translate(messages.no, props),
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.currentRegulation, props),
          value: resolveValue(
            parseFloat(
              getCurrentRegulationAmount(
                props.encryptedDocumentsOfAdults,
              ).replace(',', ''),
            ) +
              parseFloat(
                getCurrentRegulationAmount(
                  props.encryptedDocumentsOfMinors,
                ).replace(',', ''),
              ),
          ),
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
    {
      items: [
        {
          title: 'Sort appel',
          value: '',
          withBottomBorder: true,
          width: '40',
        },
        {
          title: 'Boni mali',
          value: '',
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
    {
      items: [
        {
          title: translate(messages.stopEntry, props),
          value: isStopEntry(
            props.rcCorpo,
            get(props.listings, 'PaymentMethod', []),
          )
            ? translate(messages.yes, props)
            : '',
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.feeNotes, props),
          value: getSumOfContractorFees(
            get(props.sagisClaim, 'contractorFees', []),
            get(props.listings, 'PaymentStatus', []),
          ),
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
    {
      items: [
        {
          title: 'Défense à exécution',
          value: '',
          withBottomBorder: true,
          width: '40',
        },
        {
          title: translate(messages.oldRegulation, props),
          value: getSumOfRegulations(
            props.sagisClaim,
            get(props.listings, 'PaymentStatus', []),
          ),
          withBottomBorder: true,
          width: '40',
        },
        { withBottomBorder: false, width: '20' },
      ],
    },
  ];

  const { part, abandon } = getIDDInfo(props.rcCorpo);
  const ABANDON = {
    REFUSED: translate(messages.abandonRefused, props),
    TOTAL: translate(messages.abandonTotal, props),
    PARTIAL: translate(messages.abandonPartial, props),
  } as any;
  const {
    totalRemainder,
    totalInterests,
    totalExecutionProvAmount,
    totalCNRA,
    totalJudgementInterests,
  } = getTotalEncryptedDocument(
    props.rcCorpo,
    props.corpoEncryptedDocumentsResult,
  );
  const {
    appealAmount,
    judgementAmount,
    totalAmount: totalAmountPerVictim,
  } = useMemo(() => getTotalAmount(props.rcCorpo), [props.rcCorpo]);

  return (
    <Document>
      <Page style={[styles.page]}>
        <View style={[styles.section, { justifyContent: 'flex-start' }]}>
          <View style={[styles.row, { paddingRight: 10 }]}>
            <View style={styles.arabicColumn}></View>
            <View style={[styles.arabicColumn, { width: '20%' }]}></View>
            <View style={[styles.arabicColumn, { flexDirection: 'column' }]}>
              <Text style={styles.text}>تنفيد حكم قضائي</Text>
              <Text style={styles.withUnderline}>
                {translate(messages.summaryJudicialRuling, props)}
              </Text>
              <Text style={[styles.text, styles.withUnderline]}>
                شعبة حوادث السير
              </Text>
            </View>
          </View>
        </View>
        <View style={styles.section}>
          <View style={[styles.row]}>
            <View
              style={[
                styles.frenchColumn,
                { width: '40%', alignItems: 'flex-end' },
              ]}
            >
              <Text
                style={[
                  styles.withBorder,
                  styles.boldText,
                  {
                    fontSize: 10,
                    textTransform: 'uppercase',
                    paddingTop: 3,
                  },
                ]}
              >
                {getNatureCodeTitle(
                  props.listings,
                  get(props.rcCorpo, 'regulationInfo.natureCode', ''),
                  isAppeal,
                )}
              </Text>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <View>
                  <Text style={[styles.italicText]}>
                    {translate(messages.lawyerCie, props)}
                  </Text>
                </View>

                <View style={[styles.column, { textAlign: 'right' }]}>
                  {get(props.rcCorpo, 'regulationInfo.assignedLawyer', []).map(
                    (item: any, index: number) => {
                      return (
                        <Text key={index} style={[styles.italicText]}>
                          {!isDesactivateIntervenant(
                            get(props.listings, 'intervenants'),
                            item,
                          )
                            ? getValueByCode(
                                item,
                                get(props.listings, 'intervenants'),
                              )
                            : getValueByCode(
                                item,
                                get(props.listings, 'inactiveIntervenants', []),
                              )}
                        </Text>
                      );
                    },
                  )}
                </View>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text style={[styles.italicText]}>
                  {translate(messages.lawyerPc, props)}
                </Text>
                <Text style={styles.italicText}>
                  {get(props.rcCorpo, 'regulationInfo.civilPartyLawyer')}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text style={[styles.italicText]}>
                  {translate(messages.summaryClaimNumber, props)}
                </Text>
                <Text style={styles.italicText}>
                  {get(props.sagisClaim, 'code')}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text style={[styles.italicText]}>
                  {translate(messages.summaryConnexe, props)}
                </Text>
                <Text style={styles.italicText}></Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text style={[styles.italicText]}>
                  {translate(messages.driver, props)}
                </Text>
                <Text style={styles.italicText}>
                  {!isEmpty(get(props.claimRecap, 'driver'))
                    ? `${get(props.claimRecap, 'driver.lastName')} ${get(
                        props.claimRecap,
                        'driver.firstName',
                      )}`
                    : `${get(props.claimRecap, 'claim.lastName')} ${get(
                        props.claimRecap,
                        'claim.firstName',
                      )}`}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.insuredVehicle, props)}</Text>
                <Text style={styles.italicText}>
                  {get(props.sagisClaim, 'registrationNumber')}
                </Text>
              </View>
              <Text style={[styles.withBorder, { fontSize: 10 }]}>
                {translate(messages.beneficiaries, props)}
              </Text>
            </View>
            <View
              style={[
                styles.frenchColumn,
                {
                  width: '11%',
                  flexDirection: 'column-reverse',
                  marginRight: 6,
                  marginLeft: 8,
                },
              ]}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                }}
              >
                <Text style={[styles.withBorder]}>
                  {isAppeal
                    ? translate(messages.appeal, props)
                    : translate(messages.judgement, props)}
                </Text>
              </View>
            </View>
            <View style={[styles.frenchColumn, { paddingTop: 2.5 }]}>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.date, props)}</Text>
                <Text style={styles.italicText}>
                  {moment(Date.now()).format(NORMAL_DATE_FORMAT)}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.recipient, props)}</Text>
                <Text style={styles.italicText}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.OTHERS
                    ? get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.OTHER_RECIPIENT,
                      )
                    : get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.BENEFICIARY_RECIPIENT,
                      )}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.court, props)}</Text>
                <Text style={styles.italicText}>
                  {get(props.rcCorpo.regulationInfo, REGULATIONS.COURT_NUMBER)}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.bnejNumber, props)}</Text>
                <Text style={styles.italicText}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.BNEJ
                    ? get(props.rcCorpo.regulationInfo, REGULATIONS.BNEJ_NUMBER)
                    : '***'}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text> {translate(messages.judgementOf, props)}</Text>

                <Text style={styles.italicText}>{judgementDate}</Text>
                <Text style={{ alignItems: 'center' }}>{judgementAmount}</Text>
              </View>
              {get(props.rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL]) && (
                <View
                  style={[
                    styles.row,
                    styles.withBottomBorder,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  <Text> {translate(messages.appealOf, props)}</Text>
                  <Text style={styles.italicText}>
                    {get(
                      props.rcCorpo.regulationInfo,
                      [REGULATIONS.JUDGEMENT_DATE],
                      '',
                    )}
                  </Text>
                  <Text>{appealAmount}</Text>
                </View>
              )}
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between' },
                ]}
              >
                <Text>
                  {get(props.rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL])
                    ? translate(messages.totalAppeal, props)
                    : translate(messages.totalJudgement, props)}
                </Text>
                <Text style={styles.italicText}>
                  {resolveValue(
                    getTotalAmountWithoutRC(
                      get(props.rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL])
                        ? appealAmount
                        : judgementAmount,
                      get(props.rcCorpo, 'regulationInfo.rc', ''),
                    ),
                  )}
                </Text>
              </View>
              {isAppeal && showRemainingAmountField && (
                <View
                  style={[
                    styles.row,
                    styles.withBottomBorder,
                    { justifyContent: 'space-between' },
                  ]}
                >
                  <Text>{translate(messages.remainder, props)}</Text>
                  <Text style={styles.italicText}>
                    {resolveValue(
                      parseFloat(removeCommaSeperator(appealAmount)) -
                        parseFloat(removeCommaSeperator(judgementAmount)),
                    )}
                  </Text>
                </View>
              )}
              <>
                <View
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    flexGrow: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                  }}
                >
                  {!showRemainingAmountField && (
                    <View
                      style={[
                        styles.textBorder,
                        {
                          width: showRemainingAmountField ? '15%' : 'auto',
                          border: showRemainingAmountField
                            ? '0px'
                            : '1.5px solid black',
                        },
                      ]}
                    >
                      <Text>{translate(messages.executionRate, props)}</Text>
                    </View>
                  )}
                  <View
                    style={[
                      styles.textBorder,
                      { width: showRemainingAmountField ? '25%' : 'auto' },
                    ]}
                  >
                    <Text> {translate(messages.exeProv, props)}</Text>
                  </View>
                  {showRemainingAmountField && (
                    <View style={[styles.textBorder]}>
                      <Text>{translate(messages.remainder, props)}</Text>
                    </View>
                  )}
                  <View style={[styles.textBorder, { width: '30%' }]}>
                    <Text>
                      {getCnraTitle(
                        props,
                        props.rcCorpo,
                        props.listings,
                        messages,
                      )}
                    </Text>
                  </View>
                  <View style={[styles.textBorder]}>
                    <Text> {translate(messages.interests, props)}</Text>
                  </View>
                </View>
              </>
            </View>
          </View>
        </View>
        {get(props.rcCorpo, 'data', []).map((item: any) => {
          return (
            <View>
              {!isEmpty(get(item, 'beneficiaries', [])) && (
                <View style={[styles.bloc]}>
                  <View
                    style={[
                      styles.withBottomBorder,
                      styles.row,
                      {
                        flexWrap: 'wrap',
                      },
                    ]}
                  >
                    <Text
                      style={[{ width: '34%', justifyContent: 'flex-start' }]}
                    >
                      {`${get(item, 'lastName', '')} ${get(
                        item,
                        'firstName',
                        '',
                      )}`}
                    </Text>
                    <View
                      style={{
                        width: '2%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {getVictimType(
                          item,
                          item,
                          props.rcCorpo,
                          props.sagisClaim,
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
              {get(item, 'beneficiaries', []).map((nextOfKin: any) => {
                const amountByVictim = getSummarySheetAmounts(
                  get(item, 'victimKey', ''),
                  get(nextOfKin, 'nextOfKinKey', ''),
                  props.corpoEncryptedDocumentsResult,
                );
                return (
                  <View style={[styles.bloc]}>
                    <View
                      style={[
                        styles.row,
                        styles.withBottomBorder,
                        {
                          flexWrap: 'wrap',
                          paddingLeft: 20,
                        },
                      ]}
                    >
                      <View
                        style={[
                          {
                            width: '32%',
                            justifyContent: 'flex-start',
                          },
                        ]}
                      >
                        <Text>
                          {`${get(nextOfKin, 'lastName', '')} ${get(
                            nextOfKin,
                            'firstName',
                            '',
                          )}`}
                        </Text>
                      </View>
                      <View
                        style={{
                          width: '2%',
                          alignItems: 'center',
                        }}
                      >
                        <Text>
                          {getVictimType(
                            item,
                            nextOfKin,
                            props.rcCorpo,
                            props.sagisClaim,
                          )}
                        </Text>
                      </View>
                      <View
                        style={{
                          width: '10%',
                          alignItems: 'center',
                        }}
                      >
                        <Text>
                          {get(props.rcCorpo.regulationInfo, [
                            REGULATIONS.IS_APPEAL,
                          ])
                            ? get(
                                nextOfKin.appealBeneficiary,
                                REGULATION_AMOUNTS.JUDGEMENT_AMOUNT,
                              )
                            : get(
                                nextOfKin.jomBeneficiary,
                                REGULATION_AMOUNTS.JUDGEMENT_AMOUNT,
                              )}
                        </Text>
                      </View>
                      {!showRemainingAmountField && (
                        <View
                          style={{
                            width: showRemainingAmountField ? '12%' : '17%',
                            textAlign: 'center',
                          }}
                        >
                          <Text>
                            {`${get(
                              item.regulationAmount,
                              REGULATION_AMOUNTS.RATE,
                              '',
                            )}%`}
                          </Text>
                        </View>
                      )}

                      <View
                        style={{
                          width: !showRemainingAmountField ? '12%' : '16%',
                          alignItems: !showRemainingAmountField
                            ? 'center'
                            : 'flex-end',
                        }}
                      >
                        <Text>
                          {get(
                            getSummarySheetAmounts(
                              get(item, 'victimKey', ''),
                              get(nextOfKin, 'nextOfKinKey', ''),
                              props.corpoEncryptedDocumentsResult,
                            ),
                            'provisionalExecutionAmount',
                            '',
                          )}
                        </Text>
                      </View>
                      {showRemainingAmountField && (
                        <View
                          style={{
                            width: '11%',
                            alignItems: 'center',
                          }}
                        >
                          <Text>
                            {get(amountByVictim, 'remainderAmount', '')}
                          </Text>
                        </View>
                      )}
                      <View style={{ width: '16%', alignItems: 'center' }}>
                        <Text>{get(amountByVictim, 'indemnityCNRA', '')}</Text>
                      </View>
                      <View style={{ width: '10.5%', alignItems: 'center' }}>
                        <Text>
                          {get(props.rcCorpo.regulationInfo, [
                            REGULATIONS.IS_APPEAL,
                          ])
                            ? get(amountByVictim, 'interestArrestAmount', '')
                            : get(amountByVictim, 'interestProvAmount', '')}
                        </Text>
                      </View>
                    </View>
                  </View>
                );
              })}
              {featureService.hasFeature(FEATURES.JUDGEMENT_ON_MERITS_INFO) && (
                <View>
                  <View>
                    {!!get(
                      item.regulationAmount,
                      REGULATION_AMOUNTS.FUNERAL_FEES,
                      '',
                    ) && (
                      <View style={[styles.bloc]}>
                        <View
                          style={[
                            styles.row,
                            styles.withBottomBorder,
                            {
                              flexWrap: 'wrap',
                              paddingLeft: 20,
                            },
                          ]}
                        >
                          <View
                            style={[
                              {
                                width: '32%',
                                justifyContent: 'flex-start',
                              },
                            ]}
                          >
                            <Text>
                              {translate(messages.funeralFees, props)}
                            </Text>
                          </View>
                          <View
                            style={{
                              width: '12%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.FUNERAL_FEES,
                                '',
                              )}
                            </Text>
                          </View>
                          {!showRemainingAmountField && (
                            <View
                              style={{
                                width: showRemainingAmountField ? '12%' : '17%',
                                textAlign: 'center',
                              }}
                            >
                              <Text>
                                {`${get(
                                  item.regulationAmount,
                                  REGULATION_AMOUNTS.RATE,
                                  '',
                                )}%`}
                              </Text>
                            </View>
                          )}
                          <View
                            style={{
                              width: !showRemainingAmountField ? '12%' : '16%',
                              textAlign: !showRemainingAmountField
                                ? 'center'
                                : 'right',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.FUNERAL_FEES,
                                ),
                                'provisionalExecutionAmount',
                                '',
                              )}
                            </Text>
                          </View>
                          {showRemainingAmountField && (
                            <View
                              style={{ width: '11%', alignItems: 'center' }}
                            >
                              <Text>
                                {get(
                                  getSummarySheetAmounts(
                                    get(item, 'victimKey', ''),
                                    -1,
                                    props.corpoEncryptedDocumentsResult,
                                    FEES.FUNERAL_FEES,
                                  ),
                                  'remainderAmount',
                                  '',
                                )}
                              </Text>
                            </View>
                          )}
                          <View style={{ width: '16%', alignItems: 'center' }}>
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.FUNERAL_FEES,
                                ),
                                'indemnityCNRA',
                                '',
                              )}
                            </Text>
                          </View>
                          <View style={{ width: '10.5%', textAlign: 'center' }}>
                            <Text>
                              {get(props.rcCorpo.regulationInfo, [
                                REGULATIONS.IS_APPEAL,
                              ])
                                ? get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.FUNERAL_FEES,
                                    ),
                                    'interestArrestAmount',
                                    '',
                                  )
                                : get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.FUNERAL_FEES,
                                    ),
                                    'interestProvAmount',
                                    '',
                                  )}
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                  <View>
                    {!!get(
                      item.regulationAmount,
                      REGULATION_AMOUNTS.MEDICAL_FEES,
                      '',
                    ) && (
                      <View style={[styles.bloc]}>
                        <View
                          style={[
                            styles.row,
                            styles.withBottomBorder,
                            {
                              flexWrap: 'wrap',
                              paddingLeft: 20,
                            },
                          ]}
                        >
                          <View
                            style={[
                              {
                                width: '32%',
                                justifyContent: 'flex-start',
                              },
                            ]}
                          >
                            <Text>
                              {translate(messages.medicalFees, props)}
                            </Text>
                          </View>
                          <View
                            style={{
                              width: '12%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.MEDICAL_FEES,
                                '',
                              )}
                            </Text>
                          </View>
                          {!showRemainingAmountField && (
                            <View
                              style={{
                                width: showRemainingAmountField ? '12%' : '17%',
                                textAlign: 'center',
                              }}
                            >
                              {!showRemainingAmountField && (
                                <Text>
                                  {`${get(
                                    item.regulationAmount,
                                    REGULATION_AMOUNTS.RATE,
                                    '',
                                  )}%`}
                                </Text>
                              )}
                            </View>
                          )}
                          <View
                            style={{
                              width: !showRemainingAmountField ? '12%' : '16%',
                              textAlign: !showRemainingAmountField
                                ? 'center'
                                : 'right',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.MEDICAL_FEES,
                                ),
                                'provisionalExecutionAmount',
                                '',
                              )}
                            </Text>
                          </View>
                          {showRemainingAmountField && (
                            <View
                              style={{ width: '11%', alignItems: 'center' }}
                            >
                              <Text>
                                {get(
                                  getSummarySheetAmounts(
                                    get(item, 'victimKey', ''),
                                    -1,
                                    props.corpoEncryptedDocumentsResult,
                                    FEES.MEDICAL_FEES,
                                  ),
                                  'remainderAmount',
                                  '',
                                )}
                              </Text>
                            </View>
                          )}
                          <View
                            style={{
                              width: '16%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.MEDICAL_FEES,
                                ),
                                'indemnityCNRA',
                                '',
                              )}
                            </Text>
                          </View>
                          <View
                            style={{
                              width: '10%',
                              textAlign: 'center',
                            }}
                          >
                            <Text>
                              {get(props.rcCorpo.regulationInfo, [
                                REGULATIONS.IS_APPEAL,
                              ])
                                ? get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.MEDICAL_FEES,
                                    ),
                                    'interestArrestAmount',
                                    '',
                                  )
                                : get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.MEDICAL_FEES,
                                    ),
                                    'interestProvAmount',
                                    '',
                                  )}
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              )}
              {isEmpty(get(item, 'beneficiaries', [])) && (
                <View style={[styles.bloc]}>
                  <View
                    style={[
                      styles.withBottomBorder,
                      styles.row,
                      {
                        flexWrap: 'wrap',
                      },
                    ]}
                  >
                    <Text
                      style={[
                        {
                          width: '34%',
                          justifyContent: 'flex-start',
                        },
                      ]}
                    >
                      {`${get(item, 'lastName', '')} ${get(
                        item,
                        'firstName',
                        '',
                      )}`}
                    </Text>
                    <View
                      style={{
                        width: '2%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {getVictimType(
                          item,
                          item,
                          props.rcCorpo,
                          props.sagisClaim,
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        width: '10%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? resolveValue(
                              get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.APPEAL_AMOUNT,
                              ),
                            )
                          : resolveValue(
                              get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.COMPENSATION_AMOUNT,
                              ),
                            )}
                      </Text>
                    </View>

                    {!showRemainingAmountField && (
                      <View
                        style={{
                          width: showRemainingAmountField ? '8%' : '14%',
                          textAlign: 'center',
                          marginLeft: '15px',
                        }}
                      >
                        <Text>
                          {`${get(
                            item.regulationAmount,
                            REGULATION_AMOUNTS.RATE,
                            '',
                          )}%`}
                        </Text>
                      </View>
                    )}

                    <View
                      style={{
                        width: !showRemainingAmountField ? '11%' : '15%',
                        textAlign: !showRemainingAmountField
                          ? 'center'
                          : 'right',
                      }}
                    >
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            -1,
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'provisionalExecutionAmount',
                          '',
                        )}
                      </Text>
                    </View>
                    {showRemainingAmountField && (
                      <View style={{ width: '11%', alignItems: 'center' }}>
                        <Text>
                          {get(
                            getSummarySheetAmounts(
                              get(item, 'victimKey', ''),
                              -1,
                              props.corpoEncryptedDocumentsResult,
                            ),
                            'remainderAmount',
                            '',
                          )}
                        </Text>
                      </View>
                    )}
                    <View style={{ width: '16%', alignItems: 'center' }}>
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            -1,
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'indemnityCNRA',
                          '',
                        )}
                      </Text>
                    </View>
                    <View style={{ width: '9%', alignItems: 'center' }}>
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                -1,
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestArrestAmount',
                              '',
                            )
                          : get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                -1,
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestProvAmount',
                              '',
                            )}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>
          );
        })}
        {[
          ...Array(
            15 - getVictimFieldLength(props.rcCorpo) < 0
              ? 0
              : 15 - getVictimFieldLength(props.rcCorpo),
          ),
        ]
          .map((e, i) => i + 1)
          .map((item: any) => (
            <View style={styles.bloc}>
              <Text style={[styles.withBottomBorder, { lineHeight: 3 }]}></Text>
            </View>
          ))}
        <View style={styles.section}>
          <View
            style={[
              styles.row,
              {
                padding: '0px 10px 0px 10px',
                justifyContent: 'flex-end',
              },
            ]}
          >
            <Text
              style={[
                styles.boldText,
                {
                  width: '35%',
                  textAlign: 'center',
                },
              ]}
            >
              {translate(messages.total, props)}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: '16%',
                  textAlign: !showRemainingAmountField ? 'center' : undefined,
                },
              ]}
            >
              {totalAmountPerVictim}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: '14%',
                  textAlign: 'center',
                  marginLeft: !showRemainingAmountField ? '80px' : '20px',
                },
              ]}
            >
              {totalExecutionProvAmount}
            </Text>
            {showRemainingAmountField && (
              <Text
                style={[
                  styles.withTopBorder,
                  { width: '10%', textAlign: 'center' },
                ]}
              >
                {totalRemainder}
              </Text>
            )}
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: !showRemainingAmountField ? '15%' : '20%',
                  textAlign: 'center',
                },
              ]}
            >
              {totalCNRA}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                { width: '10%', textAlign: 'center' },
              ]}
            >
              {totalInterests}
            </Text>
          </View>
        </View>
        <View style={[styles.table]}>
          <View style={styles.tableRow}>
            <View
              style={[
                styles.tableCol,
                styles.row,
                {
                  width: '30%',
                  justifyContent: 'space-between',
                },
              ]}
            >
              <Text style={[styles.item]}>
                {translate(messages.withIDD, props)}
              </Text>
              <Text style={[styles.item]}>
                {get(props.rcCorpo.regulationInfo, REGULATIONS.IS_IDD_CHECKED)}
              </Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.row,
                {
                  width: '30%',
                  justifyContent: 'space-between',
                },
              ]}
            >
              <Text style={[styles.item]}>
                {translate(messages.abandon, props)}
              </Text>
              <Text style={[styles.item]}>{ABANDON[abandon]}</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.row,
                {
                  width: '35%',
                  justifyContent: 'space-between',
                },
              ]}
            >
              <Text style={[styles.item]}>
                {translate(messages.part, props)}
              </Text>
              <Text style={[styles.item]}>{`${part}%`}</Text>
            </View>
          </View>
          <View
            style={[
              styles.tableCol,
              styles.row,
              { justifyContent: 'space-between' },
            ]}
          >
            <Text style={[styles.item]}>
              {translate(messages.mainCompensation, props)}
            </Text>
            <Text style={[styles.item]}>{totalAmountPerVictim}</Text>
          </View>
          <View
            style={[
              styles.tableCol,
              styles.row,
              { justifyContent: 'space-between' },
            ]}
          >
            <Text
              style={{
                padding: '0px 5px 0px 5px',
                fontWeight: 'bold',
              }}
            >
              {translate(messages.executionAmount, props)}
            </Text>

            <Text
              style={{
                padding: '0px 5px 0px 5px',
              }}
            >
              {!isAppeal && !showRemainingAmountField
                ? resolveValue(
                    parseFloat(
                      removeCommaSeperator(String(totalExecutionProvAmount)),
                    ) + parseFloat(removeCommaSeperator(String(totalCNRA))),
                  )
                : resolveValue(totalExecutionProvAmount)}
            </Text>
          </View>

          {isAppeal && (
            <View
              style={[
                styles.tableCol,
                styles.row,
                { justifyContent: 'space-between' },
              ]}
            >
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                  fontWeight: 'bold',
                }}
              >
                {translate(messages.legalInterests, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {resolveValue(
                  parseFloat(removeCommaSeperator(appealAmount)) -
                    parseFloat(removeCommaSeperator(judgementAmount)),
                )}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {translate(messages.from, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {appealDate}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {translate(messages.to, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {formatDate(
                  get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.REGULATION_DATE,
                    '',
                  ),
                )}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {resolveValue(totalInterests)}
              </Text>
            </View>
          )}
          {props.hasJom && (
            <View
              style={[
                styles.tableCol,
                styles.row,
                { justifyContent: 'space-between' },
              ]}
            >
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                  fontWeight: 'bold',
                }}
              >
                {translate(messages.legalInterests, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {!isAppeal && !showRemainingAmountField
                  ? resolveValue(
                      parseFloat(
                        removeCommaSeperator(String(totalExecutionProvAmount)),
                      ) + parseFloat(removeCommaSeperator(String(totalCNRA))),
                    )
                  : resolveValue(
                      parseFloat(removeCommaSeperator(judgementAmount)) -
                        parseFloat(
                          removeCommaSeperator(
                            String(totalExecutionProvAmount),
                          ),
                        ),
                    )}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {translate(messages.from, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {judgementDate}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {translate(messages.to, props)}
              </Text>
              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {formatDate(
                  get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.REGULATION_DATE,
                    '',
                  ),
                )}
              </Text>

              <Text
                style={{
                  padding: '0px 5px 0px 5px',
                }}
              >
                {resolveValue(totalJudgementInterests)}
              </Text>
            </View>
          )}

          <View style={styles.tableRow}>
            <View
              style={[
                styles.tableCol,
                styles.row,
                {
                  width: '66.5%',
                  justifyContent: 'center',
                },
              ]}
            >
              <Text>{translate(messages.regulationDetails, props)}</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.row,
                {
                  width: '16.5%',
                  justifyContent: 'center',
                },
              ]}
            >
              <Text>{translate(messages.cnra, props)}</Text>
            </View>
            <View
              style={[
                styles.row,
                styles.tableCol,
                {
                  width: '16.5%',
                  justifyContent: 'center',
                },
              ]}
            >
              <Text>
                {get(
                  props.rcCorpo.regulationInfo,
                  REGULATIONS.BENEFICIARY_RECIPIENT,
                ) === RECIPIENT.OTHERS
                  ? get(
                      props.rcCorpo.regulationInfo,
                      REGULATIONS.OTHER_RECIPIENT,
                    )
                  : get(
                      props.rcCorpo.regulationInfo,
                      REGULATIONS.BENEFICIARY_RECIPIENT,
                    )}
              </Text>
            </View>
          </View>

          <View style={[styles.row]}>
            <View
              style={[
                styles.tableCol,
                styles.frenchColumn,
                {
                  alignItems: 'flex-start',
                  padding: '0px 5px 0px 5px',
                },
              ]}
            >
              <View
                style={[
                  styles.row,
                  {
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingBottom: 6,
                  },
                ]}
              >
                <Text> {translate(messages.tax, props)}</Text>
                <Text style={[styles.italicText]}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BNEJ_TAX_AMOUNT,
                    0,
                  )}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  {
                    width: '100%',
                    justifyContent: 'space-between',
                    paddingBottom: 6,
                  },
                ]}
              >
                <Text>{translate(messages.transportation, props)}</Text>
                <Text style={[styles.italicText]}>
                  {numberWithCommas(
                    String(
                      get(
                        props.corpoEncryptedDocumentsResult,
                        '[0].documentChiffreCorporel.BNEJ.transportAmount',
                        0,
                      ).toFixed(2),
                    ),
                  )}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  {
                    paddingBottom: 6,
                    width: '100%',
                    justifyContent: 'space-between',
                  },
                ]}
              >
                <Text>{translate(messages.treasure, props)}</Text>
                <Text style={[styles.italicText]}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.BNEJ
                    ? numberWithCommas(
                        String(
                          get(
                            props.corpoEncryptedDocumentsResult,
                            '[0].documentChiffreCorporel.BNEJ.treasureAmount',
                            0,
                          ).toFixed(2),
                        ),
                      )
                    : 0}
                </Text>
              </View>
            </View>
            <View
              style={[
                styles.frenchColumn,
                styles.tableCol,
                styles.item,
                {
                  alignItems: 'flex-start',
                },
              ]}
            >
              <Text>{translate(messages.compensation, props)}</Text>
              <Text>{translate(messages.interests, props)}</Text>
              <Text>{translate(messages.costs, props)}</Text>
              <Text>{translate(messages.fee, props)}</Text>
            </View>
            <View
              style={[
                styles.frenchColumn,
                styles.tableCol,

                {
                  alignItems: 'flex-start',
                },
              ]}
            >
              <View style={styles.row}>
                <View
                  style={[
                    styles.tableCol,
                    {
                      alignItems: 'center',
                      width: '50%',
                    },
                  ]}
                >
                  <Text>{cnraCompensationAmount}</Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: '50%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={styles.italicText}>{compensationAmount}</Text>
                </View>
              </View>
              <View style={styles.row}>
                <View
                  style={[
                    styles.tableCol,
                    {
                      alignItems: 'center',
                      width: '50%',
                    },
                  ]}
                >
                  <Text>{cnraInterestsAmount}</Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: '50%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={[styles.italicText]}>{interestsAmount}</Text>
                </View>
              </View>

              <View style={styles.row}>
                <View
                  style={[
                    styles.tableCol,
                    {
                      alignItems: 'center',
                      width: '50%',
                    },
                  ]}
                >
                  <Text>{0}</Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: '50%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={[styles.italicText]}>
                    {directIndemnityCostAmount}
                  </Text>
                </View>
              </View>
              <View style={styles.row}>
                <View
                  style={[
                    styles.tableCol,
                    {
                      alignItems: 'center',
                      width: '50%',
                    },
                  ]}
                >
                  <Text>{0}</Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: '50%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={[styles.italicText]}>{fees}</Text>
                </View>
              </View>
            </View>
          </View>
          <View style={[styles.row]}>
            <View
              style={[
                styles.row,
                styles.tableCol,
                {
                  justifyContent: 'space-between',
                  width: '100%',
                  paddingBottom: 6,
                  padding: '0px 5px 0px 5px',
                },
              ]}
            >
              <Text>{translate(messages.fee, props)}</Text>
              <Text style={[styles.italicText]}>{fees}</Text>
            </View>
            <View
              style={[
                styles.row,
                styles.tableCol,
                {
                  width: '100%',
                  justifyContent: 'flex-start',
                  paddingBottom: 6,
                  padding: '0px 5px 0px 5px',
                },
              ]}
            >
              <Text style={[styles.boldText]}>
                {translate(messages.total, props)}
              </Text>
            </View>

            <View style={styles.row}>
              <View
                style={[
                  styles.tableCol,
                  styles.boldText,
                  {
                    alignItems: 'center',
                    width: '50%',
                  },
                ]}
              >
                <Text>
                  {getCurrentRegulationAmount(props.encryptedDocumentsOfMinors)}
                </Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  {
                    width: '50%',
                    alignItems: 'center',
                  },
                ]}
              >
                <Text style={[styles.boldText]}>
                  {getCurrentRegulationAmount(props.encryptedDocumentsOfAdults)}
                </Text>
              </View>
            </View>
          </View>

          {secondFields.map((item: any, key: number) => {
            return (
              <View style={[styles.row]} key={key}>
                {item.items.map((el: any, id: number) => {
                  return (
                    <View
                      key={id}
                      style={[
                        styles.tableCol,
                        styles.row,
                        {
                          borderWidth: !el.withBottomBorder ? 0 : 1,
                          width: el.width + '%',
                          justifyContent: 'space-between',
                        },
                      ]}
                    >
                      <Text
                        style={{
                          fontWeight: 'bold',
                          padding: '0px 5px 0px 5px',
                        }}
                      >
                        {el.title}
                      </Text>
                      <Text
                        style={{
                          fontStyle: 'italic',
                          fontWeight: 'normal',
                          padding: '0px 5px 0px 5px',
                        }}
                      >
                        {el.value}
                      </Text>
                    </View>
                  );
                })}
              </View>
            );
          })}
        </View>
        <View style={[styles.row, { flexWrap: 'wrap' }]}>
          <View
            style={[styles.table, { height: 70, width: '39%', marginTop: 10 }]}
          >
            <View
              style={[
                styles.frenchColumn,
                { justifyContent: 'space-between', width: '100%' },
              ]}
            >
              <View
                style={[
                  styles.tableCol,
                  { width: '100%', alignItems: 'center' },
                ]}
              >
                <Text>{translate(messages.modificationReasons, props)}</Text>
              </View>
              <View>
                {get(props.rcCorpo, 'data', []).map((item: any) => {
                  return (
                    <>
                      {isAppeal &&
                        !isEmpty(
                          get(item, 'regulationAmount.appealReason'),
                        ) && (
                          <Text>
                            {`${item.lastName} ${item.firstName}: ${get(
                              item,
                              'regulationAmount.appealReason',
                            )}`}
                          </Text>
                        )}
                      {!isAppeal &&
                        !isEmpty(
                          get(item, 'regulationAmount.judgementReason'),
                        ) && (
                          <Text>
                            {`${item.lastName} ${item.firstName}: ${get(
                              item,
                              'regulationAmount.judgementReason',
                            )}`}
                          </Text>
                        )}
                      {!isEmpty(
                        get(
                          item?.regulationAmount,
                          REGULATION_AMOUNTS.MEDICAL_FEES_REASON,
                        ),
                      ) && (
                        <Text>
                          {`${translate(messages.medicalFeesReason, props)} ${
                            item.lastName
                          } ${item.firstName}: ${get(
                            item?.regulationAmount,
                            REGULATION_AMOUNTS.MEDICAL_FEES_REASON,
                          )}`}
                        </Text>
                      )}
                      {!isEmpty(
                        get(
                          item?.regulationAmount,
                          REGULATION_AMOUNTS.FUNERAL_FEES_REASON,
                        ),
                      ) && (
                        <Text>
                          {`${translate(messages.funeralFeesReason, props)} ${
                            item.lastName
                          } ${item.firstName}: ${get(
                            item?.regulationAmount,
                            REGULATION_AMOUNTS.FUNERAL_FEES_REASON,
                          )}`}
                        </Text>
                      )}
                      {get(item, 'beneficiaries', []).map((nextOfKin: any) => {
                        return (
                          <View>
                            {isAppeal &&
                              !isEmpty(
                                get(
                                  nextOfKin,
                                  'appealBeneficiary.appealReason',
                                ),
                              ) && (
                                <Text>
                                  {`${nextOfKin.lastName} ${
                                    nextOfKin.firstName
                                  }: ${get(
                                    nextOfKin,
                                    'appealBeneficiary.appealReason',
                                  )}`}
                                </Text>
                              )}
                            {!isAppeal &&
                              !isEmpty(
                                get(
                                  nextOfKin,
                                  'jomBeneficiary.judgementReason',
                                ),
                              ) && (
                                <Text>
                                  {`${nextOfKin.lastName} ${
                                    nextOfKin.firstName
                                  }: ${get(
                                    nextOfKin,
                                    'jomBeneficiary.judgementReason',
                                  )}`}
                                </Text>
                              )}
                          </View>
                        );
                      })}
                    </>
                  );
                })}
                {featureService.hasFeature(FEATURES.REGULATION_V2) &&
                  !isEmpty(
                    get(
                      props.provisionInfo[PROVISION_ITEM.VICTIMS],
                      'provisionReason',
                      '',
                    ),
                  ) && (
                    <Text>
                      {`${translate(messages.provisionReason, props)} 
                    ${get(
                      props.provisionInfo[PROVISION_ITEM.VICTIMS],
                      'provisionReason',
                      '',
                    )}`}
                    </Text>
                  )}
              </View>
            </View>
          </View>
        </View>
      </Page>
      <Page style={[styles.page]}>
        <View style={[styles.section, { alignItems: 'center' }]}>
          <View style={[styles.arabicColumn, { flexDirection: 'column' }]}>
            <Text style={styles.text}>تنفيد حكم قضائي</Text>
            <Text style={styles.withUnderline}>
              {translate(messages.summaryJudicialRuling, props)}
            </Text>
            <Text style={[styles.text, styles.withUnderline]}>
              شعبة حوادث السير
            </Text>
          </View>
        </View>
        <View style={styles.section}>
          <View style={styles.row}>
            <View
              style={[
                styles.frenchColumn,
                { width: '40%', alignItems: 'flex-end' },
              ]}
            >
              <Text
                style={[
                  styles.withBorder,
                  {
                    fontSize: 10,
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    paddingTop: 3,
                  },
                ]}
              >
                {getNatureCodeTitle(
                  props.listings,
                  get(props.rcCorpo, 'regulationInfo.natureCode', ''),
                  isAppeal,
                )}
              </Text>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  {
                    justifyContent: 'space-between',
                    alignItems: 'flex-end',
                    marginBottom: 10,
                  },
                ]}
              >
                <Text> {translate(messages.recipient, props)}</Text>
                <Text style={styles.italicText}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.OTHERS
                    ? get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.OTHER_RECIPIENT,
                      )
                    : get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.BENEFICIARY_RECIPIENT,
                      )}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between', alignItems: 'flex-end' },
                ]}
              >
                <Text> {translate(messages.bnejNumber, props)}</Text>
                <Text style={styles.italicText}>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.BNEJ
                    ? get(props.rcCorpo.regulationInfo, REGULATIONS.BNEJ_NUMBER)
                    : '***'}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between', alignItems: 'flex-end' },
                ]}
              >
                <Text> {translate(messages.lawyerPc, props)}</Text>
                <Text style={styles.italicText}>
                  {get(props.rcCorpo, 'regulationInfo.civilPartyLawyer')}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  {
                    justifyContent: 'space-between',
                    marginBottom: 10,
                    alignItems: 'flex-end',
                  },
                ]}
              >
                <Text> {translate(messages.court, props)}</Text>
                <Text style={styles.italicText}>
                  {get(props.rcCorpo.regulationInfo, REGULATIONS.COURT_NUMBER)}
                </Text>
              </View>
              <Text style={[styles.withBorder]}>
                {translate(messages.beneficiaries, props)}
              </Text>
            </View>
            <View
              style={[
                styles.frenchColumn,
                {
                  width: '11%',
                  flexDirection: 'column-reverse',
                },
              ]}
            >
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  width: '100%',
                }}
              >
                <Text style={[styles.withBorder]}>
                  {isAppeal
                    ? translate(messages.appeal, props)
                    : translate(messages.judgement, props)}
                </Text>
              </View>
            </View>
            <View style={[styles.frenchColumn, { paddingTop: 2.5 }]}>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between', marginTop: 22 },
                ]}
              >
                <Text> {translate(messages.date, props)}</Text>
                <Text style={styles.italicText}>
                  {moment(Date.now()).format(NORMAL_DATE_FORMAT)}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  { justifyContent: 'space-between', marginTop: 22 },
                ]}
              >
                <Text> {translate(messages.summaryClaimNumber, props)}</Text>
                <Text style={[styles.italicText]}>
                  {get(props.sagisClaim, 'code')}
                </Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.withBottomBorder,
                  {
                    justifyContent: 'space-between',
                    marginTop: 22,
                    marginBottom: 5,
                  },
                ]}
              >
                <Text style={[styles.italicText]}>
                  {translate(messages.judgementOf, props)}
                </Text>
                <Text style={styles.italicText}>
                  {get(props.rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL]) ||
                  get(props.rcCorpo.regulationInfo, [
                    REGULATIONS.JUDGEMENT_DATE,
                  ]) === NONE
                    ? get(
                        props.rcCorpo.regulationInfo,
                        [REGULATIONS.JUDGEMENT_DATE_JAF],
                        '',
                      )
                    : get(
                        props.rcCorpo.regulationInfo,
                        [REGULATIONS.JUDGEMENT_DATE],
                        '',
                      )}
                </Text>
                <Text style={{ alignItems: 'center' }}>{judgementAmount}</Text>
              </View>
              {get(props.rcCorpo.regulationInfo, [REGULATIONS.IS_APPEAL]) && (
                <View
                  style={[
                    styles.row,
                    styles.withBottomBorder,
                    {
                      justifyContent: 'space-between',
                      marginTop: 22,
                      marginBottom: 5,
                    },
                  ]}
                >
                  <Text> {translate(messages.appealOf, props)}</Text>
                  <Text style={styles.italicText}>
                    {get(
                      props.rcCorpo.regulationInfo,
                      [REGULATIONS.JUDGEMENT_DATE],
                      '',
                    )}
                  </Text>
                  <Text>{appealAmount}</Text>
                </View>
              )}
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  flexGrow: 1,
                  flexWrap: 'wrap',
                  justifyContent: 'flex-start',
                }}
              >
                {!showRemainingAmountField && (
                  <View
                    style={[
                      styles.textBorder,
                      {
                        width: showRemainingAmountField ? '14%' : 'auto',
                        border: showRemainingAmountField
                          ? '0px'
                          : '1.5px solid black',
                      },
                    ]}
                  >
                    <Text> {translate(messages.executionRate, props)}</Text>
                  </View>
                )}
                <View
                  style={[
                    styles.textBorder,
                    { width: showRemainingAmountField ? '25%' : 'auto' },
                  ]}
                >
                  <Text> {translate(messages.exeProv, props)}</Text>
                </View>
                {showRemainingAmountField && (
                  <View style={[styles.textBorder]}>
                    <Text>{translate(messages.remainder, props)}</Text>
                  </View>
                )}
                <View style={[styles.textBorder, { width: '30%' }]}>
                  <Text>
                    {getCnraTitle(
                      props,
                      props.rcCorpo,
                      props.listings,
                      messages,
                    )}
                  </Text>
                </View>

                <View style={[styles.textBorder]}>
                  <Text> {translate(messages.interests, props)}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
        {get(props.rcCorpo, 'data', []).map((item: any) => {
          return (
            <View>
              {!isEmpty(get(item, 'beneficiaries', [])) && (
                <View style={[styles.bloc]}>
                  <View
                    style={[
                      styles.withBottomBorder,
                      styles.row,
                      {
                        flexWrap: 'wrap',
                      },
                    ]}
                  >
                    <Text
                      style={[{ width: '34%', justifyContent: 'flex-start' }]}
                    >
                      {`${get(item, 'lastName', '')} ${get(
                        item,
                        'firstName',
                        '',
                      )}`}
                    </Text>
                    <View
                      style={{
                        width: '2%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {getVictimType(
                          item,
                          item,
                          props.rcCorpo,
                          props.sagisClaim,
                        )}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
              {get(item, 'beneficiaries', []).map((nextOfKin: any) => (
                <View style={[styles.bloc]}>
                  <View
                    style={[
                      styles.row,
                      styles.withBottomBorder,
                      {
                        flexWrap: 'wrap',
                        paddingLeft: 20,
                      },
                    ]}
                  >
                    <View
                      style={[
                        {
                          width: '32%',
                          justifyContent: 'flex-start',
                        },
                      ]}
                    >
                      <Text>
                        {`${get(nextOfKin, 'lastName', '')} ${get(
                          nextOfKin,
                          'firstName',
                          '',
                        )}`}
                      </Text>
                    </View>
                    <View
                      style={{
                        width: '2%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {getVictimType(
                          item,
                          nextOfKin,
                          props.rcCorpo,
                          props.sagisClaim,
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        width: '10%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? get(
                              nextOfKin.appealBeneficiary,
                              REGULATION_AMOUNTS.JUDGEMENT_AMOUNT,
                            )
                          : get(
                              nextOfKin.jomBeneficiary,
                              REGULATION_AMOUNTS.JUDGEMENT_AMOUNT,
                            )}
                      </Text>
                    </View>
                    {!showRemainingAmountField && (
                      <View
                        style={{
                          width: showRemainingAmountField ? '12%' : '17%',
                          textAlign: 'center',
                        }}
                      >
                        <Text>
                          {`${get(
                            item.regulationAmount,
                            REGULATION_AMOUNTS.RATE,
                            '',
                          )}%`}
                        </Text>
                      </View>
                    )}
                    <View
                      style={{
                        width: !showRemainingAmountField ? '12%' : '16%',
                        alignItems: !showRemainingAmountField
                          ? 'center'
                          : 'flex-end',
                      }}
                    >
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            get(nextOfKin, 'nextOfKinKey', ''),
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'provisionalExecutionAmount',
                          '',
                        )}
                      </Text>
                    </View>
                    {showRemainingAmountField && (
                      <View
                        style={{
                          width: '11%',
                          alignItems: 'center',
                        }}
                      >
                        <Text>
                          {get(
                            getSummarySheetAmounts(
                              get(item, 'victimKey', ''),
                              get(nextOfKin, 'nextOfKinKey', ''),
                              props.corpoEncryptedDocumentsResult,
                            ),
                            'remainderAmount',
                            '',
                          )}
                        </Text>
                      </View>
                    )}
                    <View style={{ width: '16%', alignItems: 'center' }}>
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            get(nextOfKin, 'nextOfKinKey', ''),
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'indemnityCNRA',
                          '',
                        )}
                      </Text>
                    </View>
                    <View style={{ width: '10%', textAlign: 'center' }}>
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                get(nextOfKin, 'nextOfKinKey', ''),
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestArrestAmount',
                              '',
                            )
                          : get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                get(nextOfKin, 'nextOfKinKey', ''),
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestProvAmount',
                              '',
                            )}
                      </Text>
                    </View>
                  </View>
                </View>
              ))}
              {featureService.hasFeature(FEATURES.JUDGEMENT_ON_MERITS_INFO) && (
                <View>
                  <View>
                    {!!get(
                      item.regulationAmount,
                      REGULATION_AMOUNTS.FUNERAL_FEES,
                      '',
                    ) && (
                      <View style={[styles.bloc]}>
                        <View
                          style={[
                            styles.row,
                            styles.withBottomBorder,
                            {
                              flexWrap: 'wrap',
                              paddingLeft: 20,
                            },
                          ]}
                        >
                          <View
                            style={[
                              {
                                width: '32%',
                                justifyContent: 'flex-start',
                              },
                            ]}
                          >
                            <Text>
                              {translate(messages.funeralFees, props)}
                            </Text>
                          </View>
                          <View
                            style={{
                              width: '12%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.FUNERAL_FEES,
                                '',
                              )}
                            </Text>
                          </View>
                          {!showRemainingAmountField && (
                            <View
                              style={{
                                width: showRemainingAmountField ? '12%' : '17%',
                                textAlign: 'center',
                              }}
                            >
                              <Text>
                                {`${get(
                                  item.regulationAmount,
                                  REGULATION_AMOUNTS.RATE,
                                  '',
                                )}%`}
                              </Text>
                            </View>
                          )}
                          <View
                            style={{
                              width: !showRemainingAmountField ? '12%' : '16%',
                              textAlign: !showRemainingAmountField
                                ? 'center'
                                : 'right',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.FUNERAL_FEES,
                                ),
                                'provisionalExecutionAmount',
                                '',
                              )}
                            </Text>
                          </View>
                          {showRemainingAmountField && (
                            <View
                              style={{ width: '11%', alignItems: 'center' }}
                            >
                              <Text>
                                {get(
                                  getSummarySheetAmounts(
                                    get(item, 'victimKey', ''),
                                    -1,
                                    props.corpoEncryptedDocumentsResult,
                                    FEES.FUNERAL_FEES,
                                  ),
                                  'remainderAmount',
                                  '',
                                )}
                              </Text>
                            </View>
                          )}
                          <View style={{ width: '16%', alignItems: 'center' }}>
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.FUNERAL_FEES,
                                ),
                                'indemnityCNRA',
                                '',
                              )}
                            </Text>
                          </View>
                          <View style={{ width: '10.5%', textAlign: 'center' }}>
                            <Text>
                              {get(props.rcCorpo.regulationInfo, [
                                REGULATIONS.IS_APPEAL,
                              ])
                                ? get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.FUNERAL_FEES,
                                    ),
                                    'interestArrestAmount',
                                    '',
                                  )
                                : get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.FUNERAL_FEES,
                                    ),
                                    'interestProvAmount',
                                    '',
                                  )}
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                  <View>
                    {!!get(
                      item.regulationAmount,
                      REGULATION_AMOUNTS.MEDICAL_FEES,
                      '',
                    ) && (
                      <View style={[styles.bloc]}>
                        <View
                          style={[
                            styles.row,
                            styles.withBottomBorder,
                            {
                              flexWrap: 'wrap',
                              paddingLeft: 20,
                            },
                          ]}
                        >
                          <View
                            style={[
                              {
                                width: '32%',
                                justifyContent: 'flex-start',
                              },
                            ]}
                          >
                            <Text>
                              {translate(messages.medicalFees, props)}
                            </Text>
                          </View>
                          <View
                            style={{
                              width: '12%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                item.regulationAmount,
                                REGULATION_AMOUNTS.MEDICAL_FEES,
                                '',
                              )}
                            </Text>
                          </View>
                          {!showRemainingAmountField && (
                            <View
                              style={{
                                width: showRemainingAmountField ? '12%' : '17%',
                                textAlign: 'center',
                              }}
                            >
                              <Text>
                                {`${get(
                                  item.regulationAmount,
                                  REGULATION_AMOUNTS.RATE,
                                  '',
                                )}%`}
                              </Text>
                            </View>
                          )}
                          <View
                            style={{
                              width: !showRemainingAmountField ? '12%' : '16%',
                              textAlign: !showRemainingAmountField
                                ? 'center'
                                : 'right',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.MEDICAL_FEES,
                                ),
                                'provisionalExecutionAmount',
                                '',
                              )}
                            </Text>
                          </View>
                          {showRemainingAmountField && (
                            <View
                              style={{ width: '11%', alignItems: 'center' }}
                            >
                              <Text>
                                {get(
                                  getSummarySheetAmounts(
                                    get(item, 'victimKey', ''),
                                    -1,
                                    props.corpoEncryptedDocumentsResult,
                                    FEES.MEDICAL_FEES,
                                  ),
                                  'remainderAmount',
                                  '',
                                )}
                              </Text>
                            </View>
                          )}
                          <View
                            style={{
                              width: '16%',
                              alignItems: 'center',
                            }}
                          >
                            <Text>
                              {get(
                                getSummarySheetAmounts(
                                  get(item, 'victimKey', ''),
                                  -1,
                                  props.corpoEncryptedDocumentsResult,
                                  FEES.MEDICAL_FEES,
                                ),
                                'indemnityCNRA',
                                '',
                              )}
                            </Text>
                          </View>
                          <View style={{ width: '10%', textAlign: 'center' }}>
                            <Text>
                              {get(props.rcCorpo.regulationInfo, [
                                REGULATIONS.IS_APPEAL,
                              ])
                                ? get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.MEDICAL_FEES,
                                    ),
                                    'interestArrestAmount',
                                    '',
                                  )
                                : get(
                                    getSummarySheetAmounts(
                                      get(item, 'victimKey', ''),
                                      -1,
                                      props.corpoEncryptedDocumentsResult,
                                      FEES.MEDICAL_FEES,
                                    ),
                                    'interestProvAmount',
                                    '',
                                  )}
                            </Text>
                          </View>
                        </View>
                      </View>
                    )}
                  </View>
                </View>
              )}
              {isEmpty(get(item, 'beneficiaries', [])) && (
                <View style={[styles.bloc]}>
                  <View
                    style={[
                      styles.withBottomBorder,
                      styles.row,
                      {
                        flexWrap: 'wrap',
                      },
                    ]}
                  >
                    <Text
                      style={[
                        {
                          width: '34%',
                          justifyContent: 'flex-start',
                        },
                      ]}
                    >
                      {`${get(item, 'lastName', '')} ${get(
                        item,
                        'firstName',
                        '',
                      )}`}
                    </Text>
                    <View
                      style={{
                        width: '2%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {getVictimType(
                          item,
                          item,
                          props.rcCorpo,
                          props.sagisClaim,
                        )}
                      </Text>
                    </View>
                    <View
                      style={{
                        width: '10%',
                        alignItems: 'center',
                      }}
                    >
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? get(
                              item.regulationAmount,
                              REGULATION_AMOUNTS.APPEAL_AMOUNT,
                            )
                          : get(
                              item.regulationAmount,
                              REGULATION_AMOUNTS.COMPENSATION_AMOUNT,
                            )}
                      </Text>
                    </View>
                    {!showRemainingAmountField && (
                      <View
                        style={{
                          width: showRemainingAmountField ? '8%' : '14%',
                          textAlign: 'center',
                          marginLeft: '15px',
                        }}
                      >
                        <Text>
                          {`${get(
                            item.regulationAmount,
                            REGULATION_AMOUNTS.RATE,
                            '',
                          )}%`}
                        </Text>
                      </View>
                    )}
                    <View
                      style={{
                        width: !showRemainingAmountField ? '11%' : '15%',
                        textAlign: !showRemainingAmountField
                          ? 'center'
                          : 'right',
                      }}
                    >
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            -1,
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'provisionalExecutionAmount',
                          '',
                        )}
                      </Text>
                    </View>
                    {showRemainingAmountField && (
                      <View style={{ width: '11%', alignItems: 'center' }}>
                        <Text>
                          {get(
                            getSummarySheetAmounts(
                              get(item, 'victimKey', ''),
                              -1,
                              props.corpoEncryptedDocumentsResult,
                            ),
                            'remainderAmount',
                            '',
                          )}
                        </Text>
                      </View>
                    )}
                    <View style={{ width: '16%', alignItems: 'center' }}>
                      <Text>
                        {get(
                          getSummarySheetAmounts(
                            get(item, 'victimKey', ''),
                            -1,
                            props.corpoEncryptedDocumentsResult,
                          ),
                          'indemnityCNRA',
                          '',
                        )}
                      </Text>
                    </View>
                    <View style={{ width: '10%', alignItems: 'center' }}>
                      <Text>
                        {get(props.rcCorpo.regulationInfo, [
                          REGULATIONS.IS_APPEAL,
                        ])
                          ? get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                -1,
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestArrestAmount',
                              '',
                            )
                          : get(
                              getSummarySheetAmounts(
                                get(item, 'victimKey', ''),
                                -1,
                                props.corpoEncryptedDocumentsResult,
                              ),
                              'interestProvAmount',
                              '',
                            )}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>
          );
        })}
        {[
          ...Array(
            20 - getVictimFieldLength(props.rcCorpo) < 0
              ? 0
              : 20 - getVictimFieldLength(props.rcCorpo),
          ),
        ]
          .map((e, i) => i + 1)
          .map((item: any) => (
            <View style={styles.bloc}>
              <Text style={[styles.withBottomBorder, { lineHeight: 3 }]}></Text>
            </View>
          ))}
        <View style={styles.section}>
          <View
            style={[
              styles.row,
              {
                padding: '0px 10px 0px 10px',
                justifyContent: 'flex-end',
              },
            ]}
          >
            <Text
              style={[
                styles.boldText,
                {
                  width: '35%',
                  textAlign: 'left',
                },
              ]}
            >
              {translate(messages.total, props)}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: '16%',
                  textAlign: !showRemainingAmountField ? 'center' : undefined,
                },
              ]}
            >
              {totalAmountPerVictim}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: '14%',
                  textAlign: 'center',
                  marginLeft: !showRemainingAmountField ? '80px' : '20px',
                },
              ]}
            >
              {totalExecutionProvAmount}
            </Text>
            {showRemainingAmountField && (
              <Text
                style={[
                  styles.withTopBorder,
                  { width: '10%', alignItems: 'center' },
                ]}
              >
                {totalRemainder}
              </Text>
            )}
            <Text
              style={[
                styles.withTopBorder,
                {
                  width: !showRemainingAmountField ? '15%' : '20%',
                  textAlign: 'center',
                },
              ]}
            >
              {totalCNRA}
            </Text>
            <Text
              style={[
                styles.withTopBorder,
                { width: '10%', textAlign: 'center' },
              ]}
            >
              {totalInterests}
            </Text>
          </View>
          <View style={[styles.table]}>
            <View
              style={[
                styles.tableCol,
                styles.row,
                { justifyContent: 'space-between' },
              ]}
            >
              <Text style={[styles.item]}>
                {translate(messages.mainCompensation, props)}
              </Text>
              <Text style={[styles.item]}>{totalAmountPerVictim}</Text>
            </View>
            <View
              style={[
                styles.tableCol,
                styles.row,
                { justifyContent: 'space-between' },
              ]}
            >
              <Text
                style={[
                  styles.item,
                  {
                    fontWeight: 'bold',
                  },
                ]}
              >
                {translate(messages.executionAmount, props)}
              </Text>
              <Text
                style={[
                  styles.item,
                  {
                    textAlign: 'center',
                  },
                ]}
              >
                {!isAppeal && !showRemainingAmountField
                  ? resolveValue(
                      parseFloat(
                        removeCommaSeperator(String(totalExecutionProvAmount)),
                      ) + parseFloat(removeCommaSeperator(String(totalCNRA))),
                    )
                  : resolveValue(totalExecutionProvAmount)}
              </Text>
            </View>
            <View style={styles.tableRow}>
              <View
                style={[
                  styles.tableCol,
                  styles.row,
                  {
                    width: '66.5%',
                    justifyContent: 'center',
                  },
                ]}
              >
                <Text>{translate(messages.regulationDetails, props)}</Text>
              </View>
              <View
                style={[
                  styles.tableCol,
                  styles.row,
                  {
                    width: '16.5%',
                    justifyContent: 'center',
                  },
                ]}
              >
                <Text>{translate(messages.cnra, props)}</Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.tableCol,
                  {
                    width: '16.5%',
                    justifyContent: 'center',
                  },
                ]}
              >
                <Text>
                  {get(
                    props.rcCorpo.regulationInfo,
                    REGULATIONS.BENEFICIARY_RECIPIENT,
                  ) === RECIPIENT.OTHERS
                    ? get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.OTHER_RECIPIENT,
                      )
                    : get(
                        props.rcCorpo.regulationInfo,
                        REGULATIONS.BENEFICIARY_RECIPIENT,
                      )}
                </Text>
              </View>
            </View>
            <View style={[styles.row]}>
              <View
                style={[
                  styles.tableCol,
                  styles.frenchColumn,
                  styles.item,
                  {
                    alignItems: 'flex-start',
                  },
                ]}
              >
                <View
                  style={[
                    styles.row,
                    {
                      justifyContent: 'space-between',
                      width: '100%',
                      paddingBottom: 6,
                    },
                  ]}
                >
                  <Text> {translate(messages.tax, props)}</Text>
                  <Text style={[styles.italicText]}>
                    {get(
                      props.rcCorpo.regulationInfo,
                      REGULATIONS.BNEJ_TAX_AMOUNT,
                      0,
                    )}
                  </Text>
                </View>
                <View
                  style={[
                    styles.row,
                    {
                      width: '100%',
                      justifyContent: 'space-between',
                      paddingBottom: 6,
                    },
                  ]}
                >
                  <Text> {translate(messages.transportation, props)}</Text>
                  <Text style={[styles.italicText]}>
                    {numberWithCommas(
                      String(
                        get(
                          props.corpoEncryptedDocumentsResult,
                          '[0].documentChiffreCorporel.BNEJ.transportAmount',
                          0,
                        ).toFixed(2),
                      ),
                    )}
                  </Text>
                </View>
                <View
                  style={[
                    styles.row,
                    {
                      paddingBottom: 6,
                      width: '100%',
                      justifyContent: 'space-between',
                    },
                  ]}
                >
                  <Text> {translate(messages.treasure, props)}</Text>
                  <Text style={[styles.italicText]}>
                    {get(
                      props.rcCorpo.regulationInfo,
                      REGULATIONS.BENEFICIARY_RECIPIENT,
                    ) === RECIPIENT.BNEJ
                      ? numberWithCommas(
                          String(
                            get(
                              props.corpoEncryptedDocumentsResult,
                              '[0].documentChiffreCorporel.BNEJ.treasureAmount',
                              0,
                            ).toFixed(2),
                          ),
                        )
                      : 0}
                  </Text>
                </View>
              </View>

              <View
                style={[
                  styles.frenchColumn,
                  styles.tableCol,
                  {
                    alignItems: 'flex-start',
                  },
                ]}
              >
                <Text>{translate(messages.compensation, props)}</Text>
                <Text>{translate(messages.interests, props)}</Text>
                <Text>{translate(messages.costs, props)}</Text>
                <Text>{translate(messages.fee, props)}</Text>
              </View>
              <View
                style={[
                  styles.frenchColumn,
                  styles.tableCol,

                  {
                    alignItems: 'flex-start',
                  },
                ]}
              >
                <View style={styles.row}>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        alignItems: 'center',
                        width: '50%',
                      },
                    ]}
                  >
                    <Text>{cnraCompensationAmount}</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        width: '50%',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Text style={styles.italicText}>{compensationAmount}</Text>
                  </View>
                </View>
                <View style={styles.row}>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        alignItems: 'center',
                        width: '50%',
                      },
                    ]}
                  >
                    <Text>{cnraInterestsAmount}</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        width: '50%',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Text style={[styles.italicText]}>{interestsAmount}</Text>
                  </View>
                </View>

                <View style={styles.row}>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        alignItems: 'center',
                        width: '50%',
                      },
                    ]}
                  >
                    <Text>{0}</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        width: '50%',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Text style={[styles.italicText]}>
                      {directIndemnityCostAmount}
                    </Text>
                  </View>
                </View>
                <View style={styles.row}>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        alignItems: 'center',
                        width: '50%',
                      },
                    ]}
                  >
                    <Text>{0}</Text>
                  </View>
                  <View
                    style={[
                      styles.tableCol,
                      {
                        width: '50%',
                        alignItems: 'center',
                      },
                    ]}
                  >
                    <Text style={[styles.italicText]}>{fees}</Text>
                  </View>
                </View>
              </View>
            </View>
            <View style={[styles.row]}>
              <View
                style={[
                  styles.row,
                  styles.tableCol,
                  {
                    justifyContent: 'space-between',
                    width: '100%',
                    paddingBottom: 6,
                    padding: '0px 5px 0px 5px',
                  },
                ]}
              >
                <Text> {translate(messages.fee, props)}</Text>
                <Text style={[styles.italicText]}>{fees}</Text>
              </View>
              <View
                style={[
                  styles.row,
                  styles.tableCol,
                  styles.item,
                  {
                    width: '100%',
                    justifyContent: 'flex-end',
                    paddingBottom: 6,
                  },
                ]}
              >
                <Text style={[styles.boldText]}>
                  {translate(messages.total, props)}
                </Text>
              </View>
              <View style={styles.row}>
                <View
                  style={[
                    styles.tableCol,
                    styles.boldText,
                    {
                      alignItems: 'center',
                      width: '50%',
                    },
                  ]}
                >
                  <Text>
                    {getCurrentRegulationAmount(
                      props.encryptedDocumentsOfMinors,
                    )}
                  </Text>
                </View>
                <View
                  style={[
                    styles.tableCol,
                    {
                      width: '50%',
                      alignItems: 'center',
                    },
                  ]}
                >
                  <Text style={[styles.boldText]}>
                    {getCurrentRegulationAmount(
                      props.encryptedDocumentsOfAdults,
                    )}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
export default SummarySheet;
